import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { compensationService } from '@/services/compensation.service'
import { configService } from '@/services/config.service'
import { Compensation, CompensationStatus } from '@shared/types/entities'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'

export function CompensationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const token = useAuthStore((state) => state.token)

  const { data, isLoading } = useQuery({
    queryKey: ['compensation'],
    queryFn: () => compensationService.list(token!),
  })

  const { data: config } = useQuery({
    queryKey: ['config', 'compensation'],
    queryFn: () => configService.getCompensationConfig(),
    staleTime: 60_000,
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const getStatusColor = (status: CompensationStatus) => {
    switch (status) {
      case CompensationStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800'
      case CompensationStatus.APPROVED:
        return 'bg-blue-100 text-blue-800'
      case CompensationStatus.PAID:
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/scout/dashboard')} className="text-gray-600 hover:text-gray-900">
                ← {t('common.back')}
              </button>
              <h1 className="text-xl font-bold text-gray-900">{t('compensation.title')}</h1>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
        ) : data ? (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">{t('compensation.totalEarned')}</div>
                <div className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(data.totals.totalEarned)}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">{t('compensation.totalPending')}</div>
                <div className="text-2xl font-bold text-yellow-600 mt-2">{formatCurrency(data.totals.totalPending)}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">{t('compensation.totalPaid')}</div>
                <div className="text-2xl font-bold text-green-600 mt-2">{formatCurrency(data.totals.totalPaid)}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">{t('compensation.totalHours')}</div>
                <div className="text-2xl font-bold text-blue-600 mt-2">{data.totals.totalHours.toFixed(2)}h</div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">{t('compensation.howItWorks')}</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>{t('compensation.explanation1', { hourlyRate: config?.hourlyRate ?? 20 })}</li>
                <li>{t('compensation.explanation2', { bonus: config?.bonusPerRegistration ?? 50 })}</li>
                <li>{t('compensation.explanation3')}</li>
              </ul>
            </div>

            {/* Compensation History */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">{t('compensation.history')}</h2>
              </div>
              <div className="overflow-x-auto">
                {data.compensations.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">{t('compensation.noHistory')}</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.period')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.hours')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.hourlyPay')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.registrations')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.bonus')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.total')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.compensations.map((comp: Compensation) => (
                        <tr key={comp.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{comp.period}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{comp.hoursWorked.toFixed(2)}h</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(comp.hourlyPay)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{comp.registrationCount}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(comp.registrationBonus)}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(comp.totalAmount)}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(comp.status)}`}>
                              {t(`compensation.status.${comp.status.toLowerCase()}`)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
